import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, ArrowRight, Database, Code, RefreshCw } from 'lucide-react';
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";

// Demo messages for the chat
const initialMessages = [
  {
    id: 1,
    sender: 'ai',
    content: "Hi! I'm your TenXer assistant. How can I help you today?",
    time: '9:55:28 PM'
  }
];

// Demo data calls (API and SQL)
const dataCalls = [
  {
    id: 'sql-1',
    type: 'sql',
    name: 'Customer Purchase Query',
    expanded: false,
    connection: 'sales_db (PostgreSQL)',
    query: `SELECT
  c.customer_id,
  c.first_name,
  c.last_name,
  p.product_name,
  p.amount,
  p.purchase_date
FROM customers c
JOIN purchases p ON c.customer_id = p.customer_id
WHERE p.purchase_date >= '2024-02-18'
  AND c.customer_id IN ('CUST-89432', 'CUST-89445')
ORDER BY p.purchase_date DESC;`,
    results: [
      {
        customer_id: "CUST-89432",
        first_name: "Sarah",
        last_name: "Johnson",
        product_name: "Premium Plan",
        amount: 299,
        purchase_date: "2024-03-15T00:00:00.000Z"
      },
      {
        customer_id: "CUST-89445",
        first_name: "Michael",
        last_name: "Chen",
        product_name: "Basic Plan",
        amount: 99,
        purchase_date: "2024-03-01T00:00:00.000Z"
      }
    ],
    execution_time: "42ms"
  },
  {
    id: 'api-1',
    type: 'api',
    name: 'getCustomerActivity',
    expanded: true,
    url: 'https://api.example.com/v1/customers/activity',
    request: {
      customerIds: ["CUST-89432", "CUST-89445"],
      activityTypes: ["login", "site_visit"],
      startDate: "2024-02-18"
    },
    response: {
      status: 200,
      data: {
        customerActivities: [
          {
            customerId: "CUST-89432",
            lastLoginDate: "2024-03-16",
            lastActivityDate: "2024-03-16",
            activityCount: 1
          },
          {
            customerId: "CUST-89445",
            lastLoginDate: "2024-03-12",
            lastActivityDate: "2024-03-12",
            activityCount: 1
          }
        ]
      }
    }
  }
];

// Demo conversation flow
const demoFlow = [
  {
    id: 2,
    sender: 'user',
    content: "Show me all customers who made a purchase in the last 30 days but haven't returned to the site.",
    time: '9:55:35 PM',
    delay: 1000
  },
  {
    id: 3,
    sender: 'assistant',
    content: `I've analyzed recent customer activity and found these patterns:

**Overview:**
- 523 customers made purchases in the last 30 days
- 142 (27%) haven't returned since their purchase

**Top Recent Cases:**
1. Sarah Johnson
   - Purchased Premium Plan ($299) on March 15
   - Last active: March 16

**Recommended Actions:**
1. Schedule follow-up calls for enterprise customers
2. Offer onboarding assistance

Would you like me to:
1. Draft re-engagement email templates
2. Prioritize customers by purchase value`,
    time: '9:55:36 PM',
    delay: 3000
  }
];

export default function ProductDemo() {
  const [messages, setMessages] = useState(initialMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [currentApiCalls, setCurrentApiCalls] = useState([]);
  const [demoStarted, setDemoStarted] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-scroll to bottom of messages container only (not the whole page)
  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start the demo conversation
  const startDemo = () => {
    if (demoStarted) return;
    setDemoStarted(true);
    
    // Add user message
    setTimeout(() => {
      setMessages(prev => [...prev, demoFlow[0]]);
      
      // Show API calls after user message
      setTimeout(() => {
        setCurrentApiCalls([dataCalls[0]]);
        
        // Show typing indicator
        setTimeout(() => {
          setIsTyping(true);
          
          // Show second API call while "typing"
          setTimeout(() => {
            setCurrentApiCalls([dataCalls[0], dataCalls[1]]);
            
            // Add assistant response and remove typing indicator
            setTimeout(() => {
              setIsTyping(false);
              setMessages(prev => [...prev, demoFlow[1]]);
            }, 2000);
          }, 1500);
        }, 1000);
      }, 800);
    }, demoFlow[0].delay);
  };

  // Reset the demo
  const resetDemo = () => {
    setMessages(initialMessages);
    setCurrentApiCalls([]);
    setIsTyping(false);
    setDemoStarted(false);
  };

  // Toggle API call details expansion
  const toggleApiCall = (id) => {
    setCurrentApiCalls(prev => 
      prev.map(call => 
        call.id === id ? {...call, expanded: !call.expanded} : call
      )
    );
  };

  // Format JSON for display
  const formatJson = (json) => {
    return JSON.stringify(json, null, 2);
  };

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Mobile placeholder */}
      <div className="md:hidden bg-card rounded-xl overflow-hidden border border-border/50 shadow-xl p-6 text-center">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <Bot className="h-8 w-8 text-primary" />
          </div>
          <h3 className="text-xl font-bold">Experience the Full Demo</h3>
          <p className="text-foreground/70">
            This interactive demo is optimized for larger screens. Please view on a tablet, laptop or desktop for the full experience.
          </p>
          <div className="flex items-center justify-center mt-4 space-x-2 text-sm text-foreground/50">
            <span>•</span>
            <span>Chat Interface</span>
            <span>•</span>
            <span>SQL Queries</span>
            <span>•</span>
            <span>API Calls</span>
            <span>•</span>
          </div>
        </div>
      </div>
      
      {/* Desktop/tablet demo */}
      <div className="hidden md:block relative bg-card rounded-xl overflow-hidden border border-border/50 shadow-xl">
        {/* Demo header with controls */}
        <div className="bg-muted/30 p-3 border-b border-border/50 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="ml-2 text-sm font-medium">TenXer Chat Demo</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 px-2 text-xs"
            onClick={resetDemo}
          >
            <RefreshCw className="h-3.5 w-3.5 mr-1" />
            Reset Demo
          </Button>
        </div>
        
        {/* Main demo container */}
        <div className="flex h-[500px]">
          {/* Chat section */}
          <div className="flex-1 flex flex-col bg-background overflow-hidden border-r border-border/50">
            {/* Messages container */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((message) => (
                <div 
                  key={message.id} 
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[80%] rounded-lg p-3 ${
                      message.sender === 'user' 
                        ? 'bg-primary text-primary-foreground ml-12' 
                        : 'bg-muted text-foreground mr-12'
                    }`}
                  >
                    {message.id === 1 ? (
                      <div className="flex items-center space-x-2 mb-1">
                        <Bot className="h-5 w-5" />
                        <span className="font-medium">TenXer Assistant</span>
                      </div>
                    ) : null}
                    
                    <div className={message.sender === 'assistant' ? 'prose prose-sm dark:prose-invert max-w-none' : ''}>
                      {message.sender === 'assistant' ? (
                        <div dangerouslySetInnerHTML={{ __html: message.content.replace(/\n/g, '<br>').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                      ) : (
                        message.content
                      )}
                    </div>
                    
                    <div className="text-xs opacity-70 mt-1 text-right">
                      {message.time}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-muted rounded-lg p-3 max-w-[80%] mr-12">
                    <div className="flex items-center space-x-2">
                      <Bot className="h-5 w-5" />
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 rounded-full bg-foreground/60 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                        <div className="w-2 h-2 rounded-full bg-foreground/60 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                        <div className="w-2 h-2 rounded-full bg-foreground/60 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input area */}
            <div className="p-3 border-t border-border/50 bg-background">
              <div className="flex items-center rounded-md border border-input bg-background px-3 py-2 text-sm">
                <input 
                  type="text" 
                  placeholder={demoStarted ? "Type your message..." : "Click 'Start Demo' to see TenXer in action"}
                  className="flex-1 bg-transparent outline-none"
                  disabled={true}
                />
                <Button 
                  size="sm" 
                  className={`ml-2 ${demoStarted ? 'opacity-50 cursor-not-allowed' : ''}`}
                  onClick={startDemo}
                  disabled={demoStarted}
                >
                  {demoStarted ? "Demo Running" : "Start Demo"}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
          
          {/* API calls sidebar */}
          <div className="w-[400px] bg-muted/30 flex flex-col">
            <div className="p-3 border-b border-border/50 bg-muted/50">
              <h3 className="text-sm font-medium flex items-center">
                <Database className="h-4 w-4 mr-1.5" />
                Data Access
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {currentApiCalls.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-4 text-muted-foreground">
                  <Code className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">Data operations will appear here when the assistant accesses your information</p>
                </div>
              ) : (
                currentApiCalls.map((apiCall) => (
                  <Card key={apiCall.id} className="overflow-hidden border-border/50 text-xs">
                    <div
                      className="p-2 bg-muted/50 flex justify-between items-center cursor-pointer border-b border-border/30"
                      onClick={() => toggleApiCall(apiCall.id)}
                    >
                      <div className="font-medium flex items-center">
                        {apiCall.type === 'sql' ? (
                          <>
                            <div className="w-5 h-5 rounded-full bg-secondary/20 flex items-center justify-center mr-2">
                              <span className="text-secondary text-[10px] font-bold">SQL</span>
                            </div>
                            {apiCall.name}
                          </>
                        ) : (
                          <>
                            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center mr-2">
                              <span className="text-primary text-[10px] font-bold">API</span>
                            </div>
                            {apiCall.name}
                          </>
                        )}
                      </div>
                      <div className={`transform transition-transform ${apiCall.expanded ? 'rotate-90' : ''}`}>
                        <ArrowRight className="h-3.5 w-3.5" />
                      </div>
                    </div>
                    
                    {apiCall.expanded && (
                      <CardContent className="p-2 space-y-2 text-xs">
                        {apiCall.type === 'sql' ? (
                          <>
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">Connection:</div>
                              <div className="font-mono bg-background/50 p-1.5 rounded text-[10px]">
                                {apiCall.connection}
                              </div>
                            </div>
                            
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">SQL Query:</div>
                              <pre className="font-mono bg-background/50 p-1.5 rounded text-[10px] overflow-x-auto whitespace-pre-wrap">
                                {apiCall.query}
                              </pre>
                            </div>
                            
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1 flex justify-between">
                                <span>Results:</span>
                                <span className="text-secondary">{apiCall.execution_time}</span>
                              </div>
                              <pre className="font-mono bg-background/50 p-1.5 rounded text-[10px] overflow-x-auto">
                                {formatJson(apiCall.results)}
                              </pre>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">URL:</div>
                              <div className="font-mono bg-background/50 p-1.5 rounded text-[10px] break-all">
                                {apiCall.url}
                              </div>
                            </div>
                            
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">Request:</div>
                              <pre className="font-mono bg-background/50 p-1.5 rounded text-[10px] overflow-x-auto">
                                {formatJson(apiCall.request)}
                              </pre>
                            </div>
                            
                            <div>
                              <div className="text-[10px] uppercase text-muted-foreground mb-1">Response:</div>
                              <pre className="font-mono bg-background/50 p-1.5 rounded text-[10px] overflow-x-auto">
                                {formatJson(apiCall.response)}
                              </pre>
                            </div>
                          </>
                        )}
                      </CardContent>
                    )}
                  </Card>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}